import { formatMoney, ListView, PageHeader, useCursorList } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { HOLDER, type HolderType, WALLET_STATUS, type WalletStatus } from "../../wallets";
import styles from "./wallets.module.css";

const HOLDERS = Object.keys(HOLDER) as HolderType[];
const STATUSES = Object.keys(WALLET_STATUS) as WalletStatus[];
export const walletFilters = filterSearch({ holder: HOLDERS, status: STATUSES });

/** Wallets (P8-16, `Console / Wallets` in the design): every wallet, by holder type and status. */
export function WalletsPage(): ReactNode {
  const search = useSearch({ from: "/console/wallets" });
  const navigate = useNavigate({ from: "/wallets" });
  const format = useCompanyFormat();
  const list = useCursorList({
    queryKey: ["wallets", search],
    fetchPage: (cursor) =>
      ok(
        api.GET("/v1/admin/wallets", {
          params: { query: { holder_type: search.holder, status: search.status, cursor, limit: 25 } },
        }),
      ),
  });
  const set = (patch: Partial<typeof search>) => navigate({ search: (s) => ({ ...s, ...patch }) });
  return (
    <>
      <PageHeader
        title="Wallets"
        description="Customer, business, partner and driver wallets: balances, statements and controls."
      />
      <ListView
        caption="Wallets"
        itemName={{ one: "wallet", other: "wallets" }}
        rows={list.rows}
        rowKey={(w) => w.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={Boolean(search.holder || search.status)}
        onResetFilters={() => navigate({ search: {} })}
        empty={{
          title: "No wallets yet",
          description: "A wallet opens with its first money: a top-up, a refund, or a partner's first earnings.",
        }}
        toolbar={
          <div className={styles.toolbar}>
            <SegmentedControl
              label="Holder"
              value={search.holder ?? "all"}
              onValueChange={(v) => set({ holder: v === "all" ? undefined : (v as HolderType) })}
              options={[{ value: "all", label: "All" }, ...HOLDERS.map((h) => ({ value: h, label: HOLDER[h].many }))]}
            />
            <Select
              label="Status"
              value={search.status ?? "any"}
              onValueChange={(v) => set({ status: v === "any" ? undefined : (v as WalletStatus) })}
              options={[
                { value: "any", label: "Any status" },
                ...STATUSES.map((s) => ({ value: s, label: WALLET_STATUS[s][0] })),
              ]}
            />
          </div>
        }
        columns={[
          {
            key: "holder",
            label: "Holder",
            render: (w) => (
              <Link to="/wallets/$walletId" params={{ walletId: w.id }} className={styles.link}>
                {w.holder.name || "Unnamed"}
              </Link>
            ),
          },
          {
            key: "holder_type",
            label: "Type",
            render: (w) => <span className={styles.muted}>{HOLDER[w.holder_type].one}</span>,
          },
          ...(["balance", "held_amount", "credit_limit", "available"] as const).map((key) => ({
            key,
            label: { balance: "Balance", held_amount: "Held", credit_limit: "Credit limit", available: "Available" }[
              key
            ],
            numeric: true,
            render: (w: (typeof list.rows)[number]) => formatMoney(w[key], { ...format, currency: w.currency }),
          })),
          {
            key: "status",
            label: "Status",
            render: (w) => <Badge tone={WALLET_STATUS[w.status][1]}>{WALLET_STATUS[w.status][0]}</Badge>,
          },
        ]}
      />
    </>
  );
}
